'use client';

import * as React from 'react';

import { cn } from '@/src/shared/lib/shadcn';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from './sheet';

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Keeps the title for assistive tech when the sheet draws its own heading. */
  hideTitle?: boolean;
  description?: string;
  onCloseAutoFocus?: (event: Event) => void;
  /** Lands focus on the title rather than the first control, for a sheet that only reads. */
  focusTitle?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * The phone form of a popover or a dialog: a modal sheet from the bottom edge
 * with a grab handle, the card-system fill and the safe-area inset under it.
 */
function BottomSheet({
  open,
  onOpenChange,
  title,
  hideTitle,
  description,
  onCloseAutoFocus,
  focusTitle,
  className,
  children,
}: BottomSheetProps) {
  const titleRef = React.useRef<HTMLHeadingElement>(null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideClose
        // Radix wires the description when there is one; an explicit undefined silences its warning otherwise.
        {...(description ? {} : { 'aria-describedby': undefined })}
        onCloseAutoFocus={onCloseAutoFocus}
        onOpenAutoFocus={
          focusTitle
            ? (event) => {
                event.preventDefault();
                titleRef.current?.focus();
              }
            : undefined
        }
        overlayClassName="bg-(--sc-scrim) motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=open]:animate-none"
        className={cn(
          'max-h-[92dvh] gap-5 overflow-y-auto rounded-t-[28px] border-0 bg-(--sc-pop) px-6 pt-2.5 pb-[calc(24px+env(safe-area-inset-bottom))]',
          // Named per state: a bare motion-reduce:animate-none loses to the sheet's own state animations.
          'shadow-(--sc-shadow-pop) motion-reduce:transition-none motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=open]:animate-none',
          className,
        )}
      >
        <span
          aria-hidden
          className="h-[5px] w-9 shrink-0 self-center rounded-full bg-(--sc-grab)"
        />
        <SheetTitle
          ref={titleRef}
          tabIndex={focusTitle ? -1 : undefined}
          className={cn('text-title outline-none', hideTitle && 'sr-only')}
        >
          {title}
        </SheetTitle>
        {description && <SheetDescription className="sr-only">{description}</SheetDescription>}
        {children}
      </SheetContent>
    </Sheet>
  );
}

export { BottomSheet };
